import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import {
    Bold,
    Italic,
    List,
    ListOrdered,
    Link as LinkIcon,
    Unlink,
    Heading1,
    Heading2,
    Heading3,
    Pilcrow,
} from "lucide-react";
import { useCallback, useEffect } from "react";
import { cn } from "@/lib/utils";

interface RichTextEditorProps {
    value?: string;
    onChange?: (value: string) => void;
    placeholder?: string;
    className?: string;
}

export function RichTextEditor({
    value = "",
    onChange,
    placeholder,
    className,
}: RichTextEditorProps) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                bulletList: { keepMarks: true, keepAttributes: false },
                orderedList: { keepMarks: true, keepAttributes: false },
            }),
            Link.configure({
                openOnClick: false,
                autolink: true,
                HTMLAttributes: {
                    class: "text-primary underline cursor-pointer",
                },
            }),
        ],
        content: value,
        onUpdate: ({ editor }) => {
            onChange?.(editor.getHTML());
        },
        editorProps: {
            attributes: {
                class:
                    "min-h-[100px] w-full rounded-md border-0 bg-transparent px-3 py-2 text-sm focus:outline-none prose prose-sm max-w-none",
                "aria-label": placeholder || "Rich text editor",
            },
        },
    });

    useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            editor.commands.setContent(value);
        }
    }, [value, editor]);

    const setLink = useCallback(() => {
        if (!editor) return;
        const previousUrl = editor.getAttributes("link").href;
        const url = window.prompt("Enter URL", previousUrl);
        if (url === null) return;
        if (url === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
        }
        // Prepend `https://` when the user enters a bare host like
        // `www.instagram.com`. Without a protocol the browser resolves the
        // href against the current origin, so the link would navigate to
        // `/p/www.instagram.com` on the portal instead of the intended site.
        // Leave URLs that already carry a protocol (`http`, `https`,
        // `mailto:`, `tel:`, `ftp://`, anchors, relative paths) untouched.
        const trimmed = url.trim();
        const href = /^([a-z][a-z0-9+.-]*:|#|\/)/i.test(trimmed)
            ? trimmed
            : `https://${trimmed}`;
        editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
    }, [editor]);

    if (!editor) return null;

    return (
        <div
            className={cn(
                "rounded-md border border-input bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
                className
            )}
        >
            <div className="flex flex-wrap gap-1 border-b border-input p-1">
                <button
                    type="button"
                    onClick={() => editor.chain().focus().setParagraph().run()}
                    className={cn(
                        "inline-flex h-8 w-8 items-center justify-center rounded-md text-sm font-medium transition-colors hover:bg-muted",
                        editor.isActive("paragraph") && !editor.isActive("heading") && "bg-muted"
                    )}
                    title="Paragraph"
                    aria-label="Set paragraph"
                >
                    <Pilcrow className="h-4 w-4" />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                    className={cn(
                        "inline-flex h-8 w-8 items-center justify-center rounded-md text-sm font-medium transition-colors hover:bg-muted",
                        editor.isActive("heading", { level: 1 }) && "bg-muted"
                    )}
                    title="Heading 1"
                    aria-label="Toggle heading 1"
                >
                    <Heading1 className="h-4 w-4" />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    className={cn(
                        "inline-flex h-8 w-8 items-center justify-center rounded-md text-sm font-medium transition-colors hover:bg-muted",
                        editor.isActive("heading", { level: 2 }) && "bg-muted"
                    )}
                    title="Heading 2"
                    aria-label="Toggle heading 2"
                >
                    <Heading2 className="h-4 w-4" />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                    className={cn(
                        "inline-flex h-8 w-8 items-center justify-center rounded-md text-sm font-medium transition-colors hover:bg-muted",
                        editor.isActive("heading", { level: 3 }) && "bg-muted"
                    )}
                    title="Heading 3"
                    aria-label="Toggle heading 3"
                >
                    <Heading3 className="h-4 w-4" />
                </button>
                <div className="w-px bg-border mx-1" />
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    className={cn(
                        "inline-flex h-8 w-8 items-center justify-center rounded-md text-sm font-medium transition-colors hover:bg-muted",
                        editor.isActive("bold") && "bg-muted"
                    )}
                    title="Bold (Ctrl+B)"
                    aria-label="Toggle bold"
                >
                    <Bold className="h-4 w-4" />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    className={cn(
                        "inline-flex h-8 w-8 items-center justify-center rounded-md text-sm font-medium transition-colors hover:bg-muted",
                        editor.isActive("italic") && "bg-muted"
                    )}
                    title="Italic (Ctrl+I)"
                    aria-label="Toggle italic"
                >
                    <Italic className="h-4 w-4" />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    className={cn(
                        "inline-flex h-8 w-8 items-center justify-center rounded-md text-sm font-medium transition-colors hover:bg-muted",
                        editor.isActive("bulletList") && "bg-muted"
                    )}
                    title="Bullet List"
                    aria-label="Toggle bullet list"
                >
                    <List className="h-4 w-4" />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    className={cn(
                        "inline-flex h-8 w-8 items-center justify-center rounded-md text-sm font-medium transition-colors hover:bg-muted",
                        editor.isActive("orderedList") && "bg-muted"
                    )}
                    title="Numbered List"
                    aria-label="Toggle numbered list"
                >
                    <ListOrdered className="h-4 w-4" />
                </button>
                <button
                    type="button"
                    onClick={setLink}
                    className={cn(
                        "inline-flex h-8 w-8 items-center justify-center rounded-md text-sm font-medium transition-colors hover:bg-muted",
                        editor.isActive("link") && "bg-muted"
                    )}
                    title="Add Link"
                    aria-label="Add link"
                >
                    <LinkIcon className="h-4 w-4" />
                </button>
                {editor.isActive("link") && (
                    <button
                        type="button"
                        onClick={() => editor.chain().focus().unsetLink().run()}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-sm font-medium transition-colors hover:bg-muted"
                        title="Remove Link"
                        aria-label="Remove link"
                    >
                        <Unlink className="h-4 w-4" />
                    </button>
                )}
            </div>
            <EditorContent editor={editor} />
        </div>
    );
}
